/**
 * StageScreen — YouTube IFrame embed positioned over a stage's screen plane.
 *
 * Three.js can't sample an iframe as a texture, so we use a sibling DOM
 * layer (`#css3d-root`) and project a div positioned absolutely to track
 * the stage screen quad in world space.
 *
 * Per the user's prior experience this approach works fine in three.js;
 * we render the iframe behind the canvas with the canvas's clearAlpha = 0
 * and rely on simple z-projection (no full CSS3D matrix), which keeps the
 * compositing predictable.
 */

import * as THREE from 'three';
import type { StageHandle } from '@/world/stages/StageBase';

export interface ScreenBinding {
  stage: StageHandle;
  videoId: string | null;
}

interface StageScreenInternal {
  stage: StageHandle;
  iframe: HTMLIFrameElement;
  container: HTMLDivElement;
  currentVideoId: string | null;
}

export class StageScreens {
  private screens = new Map<string, StageScreenInternal>();
  private root: HTMLDivElement;

  constructor(root: HTMLDivElement, private camera: THREE.PerspectiveCamera) {
    this.root = root;
  }

  bind(stage: StageHandle): void {
    if (this.screens.has(stage.id)) return;
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.transformOrigin = 'top left';
    container.style.pointerEvents = 'none';
    container.style.background = '#000';
    container.style.border = '1px solid rgba(255,195,113,0.4)';
    container.style.boxShadow = '0 0 20px rgba(0,0,0,0.55)';
    container.style.opacity = '0';
    container.dataset.stageId = stage.id;

    const iframe = document.createElement('iframe');
    iframe.allow = 'autoplay; encrypted-media';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.pointerEvents = 'none';
    container.appendChild(iframe);

    this.root.appendChild(container);

    this.screens.set(stage.id, {
      stage,
      iframe,
      container,
      currentVideoId: null
    });
  }

  setVideo(stageId: string, videoId: string | null): void {
    const s = this.screens.get(stageId);
    if (!s) return;
    if (s.currentVideoId === videoId) return;
    s.currentVideoId = videoId;
    if (!videoId) {
      s.iframe.src = '';
      return;
    }
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      controls: '0',
      loop: '1',
      playlist: videoId,
      modestbranding: '1',
      rel: '0'
    });
    s.iframe.src = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  }

  /** Project each screen onto the camera plane and update div transforms. */
  update(): void {
    for (const s of this.screens.values()) {
      // Project center of the quad to NDC
      const corners = s.stage.screenQuad;
      // Re-project corners using the stage's group world transform.
      const worldCorners = corners.map((c) => c.clone().applyMatrix4(s.stage.group.matrixWorld));
      const center = new THREE.Vector3();
      worldCorners.forEach((c) => center.add(c));
      center.multiplyScalar(1 / worldCorners.length);

      // Cull behind camera
      const dirToCenter = center.clone().sub(this.camera.position);
      const camDir = new THREE.Vector3();
      this.camera.getWorldDirection(camDir);
      if (dirToCenter.dot(camDir) < 0) {
        s.container.style.opacity = '0';
        continue;
      }

      // Project all four corners and compute the screen-space bounding rect.
      const w = window.innerWidth;
      const h = window.innerHeight;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      let valid = true;
      for (const wc of worldCorners) {
        const ndc = wc.clone().project(this.camera);
        if (ndc.z > 1 || ndc.z < -1) { valid = false; break; }
        const sx = (ndc.x * 0.5 + 0.5) * w;
        const sy = (-ndc.y * 0.5 + 0.5) * h;
        if (sx < minX) minX = sx;
        if (sy < minY) minY = sy;
        if (sx > maxX) maxX = sx;
        if (sy > maxY) maxY = sy;
      }
      if (!valid || maxX - minX < 4 || maxY - minY < 4) {
        s.container.style.opacity = '0';
        continue;
      }
      // Distance fade so the iframe doesn't pop in from a kilometer away.
      const dist = dirToCenter.length();
      const fadeIn = THREE.MathUtils.smoothstep(1 - (dist - 50) / 200, 0, 1);
      s.container.style.opacity = String(Math.max(0, Math.min(1, fadeIn)));
      s.container.style.left = `${minX}px`;
      s.container.style.top = `${minY}px`;
      s.container.style.width = `${maxX - minX}px`;
      s.container.style.height = `${maxY - minY}px`;
    }
  }
}
